import React, { useState } from 'react';
import { 
  FaGithub, 
  FaLinkedin, 
  FaEnvelope, 
  FaTwitter, 
  FaDownload,
  FaPython,
  FaJava,
  FaJs,
  FaHtml5,
  FaCss3Alt,
  FaReact,
  FaDatabase,
  FaGitAlt,
  FaDocker,
  FaAws
} from 'react-icons/fa';
import { 
  SiTensorflow,
  SiPytorch,
  SiScikitlearn,
  SiPandas,
  SiNumpy,
  SiCplusplus,
  SiR,
  SiGo,
  SiJavascript,
  SiTypescript,
  SiAssemblyscript
} from 'react-icons/si';
import profileImage from './assets/profile.jpg';
import './App.css';
import { generateSkillPositions } from './skillPositions';

function App() {
  const [activeSection, setActiveSection] = useState('home');
  const [isSkillsVisible, setIsSkillsVisible] = useState(false);

const skills = {
  // Inner orbit - Core ML/DS tools (6 items)
  innerOrbit: [
    { 
      name: 'PyTorch', 
      icon: <SiPytorch />, 
      color: '#EE4C2C', 
      category: 'ML Tools' 
    },
    { 
      name: 'TensorFlow', 
      icon: <SiTensorflow />, 
      color: '#FF6F00', 
      category: 'ML Tools' 
    },
    { 
      name: 'Scikit-Learn', 
      icon: <SiScikitlearn />, 
      color: '#F7931E', 
      category: 'ML Tools' 
    },
    { 
      name: 'Pandas', 
      icon: <SiPandas />, 
      color: '#150458', 
      category: 'ML Tools' 
    },
    { 
      name: 'NumPy', 
      icon: <SiNumpy />, 
      color: '#013243', 
      category: 'ML Tools' 
    },
    { 
      name: 'SciPy', 
      icon: 'S', 
      color: '#8CAAE6', 
      category: 'ML Tools' 
    }
  ],
  // Middle orbit - Programming languages (6 items)
  middleOrbit: [
    { 
      name: 'Python', 
      icon: <FaPython />, 
      color: '#3776AB', 
      category: 'Programming' 
    },
    { 
      name: 'R', 
      icon: <SiR />, 
      color: '#276DC3', 
      category: 'Programming' 
    },
    { 
      name: 'Java', 
      icon: <FaJava />, 
      color: '#007396', 
      category: 'Programming' 
    },
    { 
      name: 'C++', 
      icon: <SiCplusplus />, 
      color: '#00599C', 
      category: 'Programming' 
    },
    { 
      name: 'Go', 
      icon: <SiGo />, 
      color: '#00599C', 
      category: 'Programming' 
    },
    { 
      name: 'SQL', 
      icon: <FaDatabase />, 
      color: '#4479A1', 
      category: 'Programming' 
    },
    { 
      name: 'Assembly', 
      icon: <SiAssemblyscript />, 
      color: '#007AAC', 
      category: 'Programming' 
    },
    { 
      name: 'MATLAB', 
      icon: 'M', 
      color: '#0076A8', 
      category: 'Programming' 
    }
  ],
  // Outer orbit - Web technologies (4 items)
  outerOrbit: [
    { 
      name: 'JavaScript', 
      icon: <FaJs />, 
      color: '#F7DF1E', 
      category: 'Web Dev' 
    },
    { 
      name: 'HTML5', 
      icon: <FaHtml5 />, 
      color: '#E34F26', 
      category: 'Web Dev' 
    },
    { 
      name: 'CSS3', 
      icon: <FaCss3Alt />, 
      color: '#1572B6', 
      category: 'Web Dev' 
    },
    { 
      name: 'JavaFX', 
      icon: <FaJava />, 
      color: '#007396', 
      category: 'Web Dev' 
    }
  ]
};


  const allSkills = [...skills.innerOrbit, ...skills.middleOrbit, ...skills.outerOrbit];
  const skillPositions = generateSkillPositions(allSkills);

  const handleDownloadCV = () => {
    const link = document.createElement('a');
    link.href = '/ziyu_cv.pdf';
    link.download = 'ziyu_cv.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="app">
      {/* Navigation */}
      <nav className="navbar">
        <div className="logo">Ziyu.Li</div>
        <ul className="nav-links">
          <li><a href="#home">About</a></li>
          <li><a href="#research">Research</a></li>
          <li><a href="#projects">Projects</a></li>
          <li><a href="#skills">Skills</a></li>
          <li><a href="#contact">Contact</a></li>
        </ul>
        <button className="hire-btn">🫛</button>
      </nav>

      {/* Hero Section */}
      <section className="hero" id="home">
        <div className="hero-content">
          <p className="subtitle">Master Student | COMPUTATIONAL BIOLOGY</p>
          <h1>Hello I'm <span className="highlight">Ziyu</span></h1>
          <div className="description">
            <p> 
            I am a second-year Master's student in Computational Biology at Carnegie Mellon University, 
            where I conduct research in the Henderson Lab under the supervision of Prof. Maggie Henderson. 
            </p>
            <p>
            My research primarily focuses on computer vision, multimodality, and developing models that bridge human perception 
            and artificial intelligence. I am also interested in representation learning, 
            interpretable bio-inspired machine learning methods, and data-driven scientific discovery.
            </p> 
            <p>
            I previously conducted computational biology research at UC Berkeley, analyzing single-cell datasets to uncover microglial stress pathways, 
            and gained industry experience building end-to-end analytical systems at Merck.
            </p>
          </div>
          
          <div className="cta-buttons">
            <a href="/ziyu_cv.pdf" target="_blank" rel="noopener noreferrer">
              <button className="download-cv">
                <span>DOWNLOAD CV</span>  
                <FaDownload className="download-icon" />
              </button>
            </a>
            
            <div className="social-links">
              <a href="https://github.com/ziyuxli" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                <FaGithub />
              </a>              
              <a href="https://www.linkedin.com/in/ziyuxli/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <FaLinkedin />
              </a>
              <a href="mailto:ziyuli@cs.cmu.edu" aria-label="Email">
                <FaEnvelope />
              </a>
              <a href="https://twitter.com/ziyuxli" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                <FaTwitter />
              </a>
            </div>
          </div>
        </div>

        <div className="hero-image">
          <div className="image-container">
            <div className="rotating-border"></div>
            <img src={profileImage} alt="Profile" />
          </div>
        </div>
      </section>


      {/* Research Interests Section */}
      <section className="research-section" id="research">
        <div className="research-container">
          <h2>Research Interests</h2>
          <p className="section-subtitle">Areas of focus and exploration</p>
          
          <div className="interests-grid">
            <div className="interest-card">
              <div className="interest-icon">👁️</div>
              <h3>Computer Vision</h3>
              <p>Developing visual perception models that understand and interpret complex image and video data. 
              Creating models that integrate and learn from multiple data modalities to bridge human perception and AI</p>
            </div>

            <div className="interest-card">
              <div className="interest-icon">🧠</div>
              <h3>Bio-Inspired & Neuroscience-Guided ML</h3>
              <p>Designing interpretable and bio-inspired machine learning methods, 
              drawing on neuroscience and computational biology to better understand both models and brains.</p>
            </div>

            <div className="interest-card">
              <div className="interest-icon">🤝</div>
              <h3>Reinforcement Learning</h3>
              <p>Aligning AI systems with human preferences and values through feedback mechanisms, 
              enabling models to learn from human guidance and demonstrations</p>
            </div>

          </div>
        </div>
      </section>

      {/* Research Experience Section */}
      <section className="projects-section" id="projects">
        <div className="projects-container">
          <h2>Research Experience</h2>
          <p className="section-subtitle">Key projects and contributions</p>
          
          <div className="project-timeline">
            <div className="project-card">
              <div className="project-header">
                <h3>Multimodal Perception Research</h3>
                <span className="project-date">2025 - Present</span>
              </div>
              <p className="project-location">Henderson Lab, Carnegie Mellon University</p>
              <p className="project-description">
                Developing computational models that bridge human perception and artificial intelligence through 
                computer vision and multimodality approaches. Focus on implementing interpretability tools 
                to better understand  how models process visual information.
              </p>
              <div className="project-tags">
                <span className="tag">PyTorch</span>
                <span className="tag">Computer Vision</span>
                <span className="tag">Machine Learning</span>
              </div>
            </div>

            <div className="project-card">
              <div className="project-header">
                <h3>Single-Cell Genomics Analysis</h3>
                <span className="project-date">2023 Spring</span>
              </div>
              <p className="project-location">UC Berkeley</p>
              <p className="project-description">
                Analyzed single-cell RNA sequencing datasets to uncover microglial stress pathways and cellular 
                responses. Developed pipelines for processing large-scale genomic data and identifying 
                key biomarkers.
              </p>
              <div className="project-tags">
                <span className="tag">R</span>
                <span className="tag">Single-cell Analysis</span>
                <span className="tag">Bioinformatics</span>
              </div>
            </div>

            <div className="project-card">
              <div className="project-header">
                <h3>End-to-End Analytical Systems</h3>
                <span className="project-date">2023 Summer</span>
              </div>
              <p className="project-location">Merck & Co.</p>
              <p className="project-description">
                Built scalable analytical systems for processing and analyzing large pharmaceutical datasets. 
                Developed data pipelines and visualization tools that enabled data-driven decision making across 
                research teams.
              </p>
              <div className="project-tags">
                <span className="tag">Python</span>
                <span className="tag">R</span>
                <span className="tag">Data Engineering</span>
                <span className="tag">Analytics</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section with Orbital Animation */}
      <section className="skills-section" id="skills">
        <div className="skills-header">
          <h2>Technical Skills</h2>
          <p>Programming & Machine Learning Expertise</p>
        </div>

        {!isSkillsVisible ? (
          <div className="orbital-container">
            <div className="center-logo">Z</div>
            <div className="orbit orbit-1"></div>
            <div className="orbit orbit-2"></div>
            <div className="orbit orbit-3"></div>
            
            {allSkills.map((skill, index) => (
              <div 
                key={index} 
                className="skill-item"
                style={skillPositions[index]}
                title={skill.category}
              >
                <div className="skill-icon">
                  {typeof skill.icon === 'string' ? skill.icon : skill.icon}
                </div>
                <div className="skill-name">{skill.name}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="skills-grid">
            <div className="skill-category">
              <h3 className="category-title">ML & Data Science Tools</h3>
              <div className="category-skills">
                {skills.innerOrbit.map((skill, i) => (
                  <div key={i} className="skill-tag">
                    {skill.icon} {skill.name}
                  </div>
                ))}
              </div>
            </div>

            <div className="skill-category">
              <h3 className="category-title">Programming</h3>
              <div className="category-skills">
                {skills.middleOrbit.map((skill, i) => (
                  <div key={i} className="skill-tag">
                    <span className="tag-icon">
                      {typeof skill.icon === 'string' ? skill.icon : skill.icon}
                    </span>
                    <span>{skill.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="skill-category">
              <h3 className="category-title">Web Development</h3>
              <div className="category-skills">
                {skills.outerOrbit.map((skill, i) => (
                  <div key={i} className="skill-tag">
                    {skill.icon} {skill.name}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <button 
          className="view-toggle"
          onClick={() => setIsSkillsVisible(!isSkillsVisible)}
        >
          {isSkillsVisible ? '🌌 Show Orbital View' : '📋 Show Grid View'}
        </button>
      </section>

      {/* Contact Section */}
      <section className="contact-section" id="contact">
        <div className="contact-container">
          <h2>Get In Touch</h2>
          <p className="section-subtitle">Interested in collaboration or have questions?</p>
          
          <div className="contact-info">
            <a href="mailto:ziyuli@cmu.edu" className="contact-card">
              <FaEnvelope className="contact-icon" />
              <h3>Email</h3>
              <p>ziyuli@cmu.edu</p>
            </a>

            <a href="https://github.com/ziyuxli" target="_blank" rel="noopener noreferrer" className="contact-card">
              <FaGithub className="contact-icon" />
              <h3>GitHub</h3>
              <p>@ziyuxli</p>
            </a>

            <a href="https://www.linkedin.com/in/ziyuxli" target="_blank" rel="noopener noreferrer" className="contact-card">
              <FaLinkedin className="contact-icon" />
              <h3>LinkedIn</h3>
              <p>Ziyu Li</p>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <p>© 2024 Ziyu Li. Built with React.</p>
      </footer>
    </div>
  );
}

export default App;